import { DEFAULT_DECIMALS_PRECISION } from "./constants";
import { toBaseUnitsFloor } from "./tokenAmount";

const padDecimals = (decimals: number) => Number("1".padEnd(decimals + 1, "0"));

/**
 * The unsigned decimal text to truncate, without going through the binary
 * expansion of the double. `String(8.29)` is "8.29", whereas the double itself is
 * 8.28999999999999914..., and truncating the latter is what loses a unit.
 */
const toDecimalText = (amount: number | string, decimals: number) => {
  if (typeof amount === "string") return amount.trim().replace(/^[+-]/, "");

  const magnitude = Math.abs(amount);
  const shortest = String(magnitude);

  // Exponential notation ("1e-18") cannot be truncated as text, so expand it one
  // place past the unit — far enough that flooring at `decimals` stays exact.
  return /e/i.test(shortest) ? magnitude.toFixed(Math.min(100, decimals + 1)) : shortest;
};

/**
 * A human amount to the coin's smallest unit, truncating anything finer than the
 * unit can represent.
 *
 * Truncation happens on the decimal representation rather than as
 * `Math.floor(amount * 10 ** decimals)`: the multiplication drops the last unit
 * whenever the double sits just below the decimal it prints as
 * (`Math.floor(8.29 * 1e8) === 828999999`), and it cannot represent decimal
 * *text* exactly at all.
 */
const toUIntAmount = (amount: number | string, decimals: number) => {
  const numAmount = Number(amount);

  if (!Number.isFinite(numAmount) || numAmount === 0) {
    return "0";
  }

  const units = toBaseUnitsFloor(toDecimalText(amount, decimals), decimals);

  if (units === null || units === 0n) return "0";

  return `${numAmount < 0 ? "-" : ""}${units}`;
};

const fromUIntAmount = (amount: string | number, decimals: number) =>
  (Number(amount) / padDecimals(decimals)).toFixed(DEFAULT_DECIMALS_PRECISION);

const toDecimalsPrecisionString = (amount: string | number, decimals: number) =>
  (Number(amount) / padDecimals(decimals)).toFixed(DEFAULT_DECIMALS_PRECISION);

const toFormattedDigits = (amount: string | number | undefined) => {
  const formatter = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  });

  if (amount === undefined || isNaN(Number(amount))) return formatter.format(0);

  if (typeof amount === "string") {
    return formatter.format(Number(amount));
  }

  return formatter.format(amount);
};

const toFormattedString = (
  amount: string | number,
  decimals: number = 2,
  currency: string = "USD",
) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return formatter.format(Number(amount));
};

export default {
  toUIntAmount,
  fromUIntAmount,
  toDecimalsPrecisionString,
  toFormattedString,
  toFormattedDigits,
};
